// Updated InventoryPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import InventoryTable from '../../components/inventory/InventoryTable';
import InventoryFilters from '../../components/inventory/InventoryFilters';
import StatsCard from '../../components/dashboard/StatsCard';
import Button from '../../components/common/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  getProductsSimple as getProducts,
  getProductCategories, 
  getProductBrands,
  getProductStatistics,
  updateProductStock, 
  deleteProduct 
} from '../../services/inventoryService';

const InventoryPage = () => {
  const { 
    currentUser, 
    vendorProfile, 
    getAllCategories, 
    getAllBrands 
  } = useAuth();
  const { showAlert } = useNotification();
  
  // State
  const [products, setProducts] = useState([]);
  const [systemCategories, setSystemCategories] = useState([]);
  const [systemBrands, setSystemBrands] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Get user-created categories and brands from AuthContext
  const userCategories = getAllCategories();
  const userBrands = getAllBrands();
  
  // Combine system and user-created data
  const allCategories = [...systemCategories, ...userCategories];
  const allBrands = [...systemBrands, ...userBrands];
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    brand: '',
    stockFilter: 'all',
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Debounced filters
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters]);
  
  const loadProducts = async (showLoading = true) => {
    try {
      if (!vendorProfile?.uid) return;
      
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      
      console.log('Loading products with filters:', debouncedFilters);
      
      // Prepare options for the service
      const options = {
        pageSize,
        category: debouncedFilters.category || null,
        brand: debouncedFilters.brand || null,
        search: debouncedFilters.search || '',
        stockFilter: debouncedFilters.stockFilter === 'all' ? null : debouncedFilters.stockFilter,
        sortBy: debouncedFilters.sortBy || 'createdAt',
        sortDirection: debouncedFilters.sortDirection || 'desc'
      };
      
      const result = await getProducts(vendorProfile.uid, options);
      
      setProducts(result.products || []);
      setTotal(result.total || 0);
      setHasMore(result.hasMore || false);
      setSelectedProducts([]);
      
    } catch (error) {
      console.error('Error loading products:', error);
      setError(error.message || 'Failed to load products. Please try again.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Load system categories, brands, and statistics
  const loadFiltersAndStats = async () => {
    try {
      // Get all categories and filter system ones
      const allCategoriesResult = await getProductCategories();
      const systemCats = allCategoriesResult.filter(cat => !cat.isUserCreated);
      setSystemCategories(systemCats);
      
      // Get all brands and filter system ones
      const allBrandsResult = await getProductBrands();
      const systemBrands = allBrandsResult.filter(brand => !brand.isUserCreated);
      setSystemBrands(systemBrands);
      
      // Get statistics
      if (vendorProfile?.uid) {
        const statsResult = await getProductStatistics(vendorProfile.uid);
        setStatistics(statsResult);
      }
    } catch (error) {
      console.error('Error loading filters and stats:', error);
      showAlert('Some features may not be available', 'warning');
    }
  };

  // Comprehensive refresh function
  const handleRefresh = async () => {
    await Promise.all([
      loadProducts(false),
      loadFiltersAndStats()
    ]);
    showAlert('Data refreshed successfully', 'success');
  };
  
  // Initial load
  useEffect(() => {
    if (vendorProfile?.uid) {
      loadProducts();
      loadFiltersAndStats();
    }
  }, [vendorProfile?.uid]);
  
  // Load products when debounced filters change
  useEffect(() => {
    if (vendorProfile?.uid) {
      loadProducts();
      setPage(1);
    }
  }, [debouncedFilters, vendorProfile?.uid]);
  
  // Handle filter change
  const handleFilterChange = (name, value) => {
    console.log('Filter change:', name, value);
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle product selection
  const handleProductSelect = (productId, selected) => {
    if (selected) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };
  
  // Handle select all products
  const handleSelectAll = (selected) => {
    if (selected) {
      setSelectedProducts(products.map(product => product.id));
    } else {
      setSelectedProducts([]);
    }
  };
  
  // Handle stock update
  const handleStockUpdate = async (productId, newStock) => {
    try {
      await updateProductStock(productId, newStock);
      
      // Update product in state
      setProducts(prev => prev.map(product => {
        if (product.id === productId) {
          return { ...product, stock: newStock };
        }
        return product;
      }));
      
      showAlert('Stock updated successfully', 'success');
      
      // Reload statistics
      if (vendorProfile?.uid) {
        const statsResult = await getProductStatistics(vendorProfile.uid);
        setStatistics(statsResult);
      }
      
      return true;
    } catch (error) {
      console.error('Error updating stock:', error);
      showAlert('Failed to update stock', 'error');
      throw error;
    }
  };
  
  // Handle product deletion
  const handleDeleteProduct = async (productId) => {
    try {
      if (!window.confirm('Are you sure you want to delete this product?')) {
        return;
      }
      
      await deleteProduct(productId, vendorProfile.uid);
      
      // Remove product from state
      setProducts(prev => prev.filter(product => product.id !== productId));
      setSelectedProducts(prev => prev.filter(id => id !== productId));
      
      // Update statistics
      if (statistics) {
        setStatistics(prev => ({
          ...prev,
          totalProducts: prev.totalProducts - 1
        }));
      }
      
      showAlert('Product deleted successfully', 'success');
      await handleRefresh();
    } catch (error) {
      console.error('Error deleting product:', error);
      showAlert('Failed to delete product', 'error');
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Inventory Management
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage your auto parts inventory, track stock levels, and update product details.
            </p>
          </div>
          <div className="flex space-x-3">
            <Button 
              variant="secondary" 
              onClick={handleRefresh}
              disabled={loading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              Refresh
            </Button>
            <Link to="/inventory/add">
              <Button style={{ backgroundColor: 'red', borderColor: 'red', color: 'white' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Product
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 text-red-700 bg-red-100 border border-red-400 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading products</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <button
                      type="button"
                      className="px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:bg-red-200"
                      onClick={handleRefresh}
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <StatsCard
              title="Total Products"
              value={statistics.totalProducts}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              }
            />
            <StatsCard
              title="Inventory Value"
              value={formatCurrency(statistics.totalValue)}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <StatsCard
              title="Low Stock Products"
              value={statistics.lowStockProducts}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              }
              trend={statistics.lowStockProducts > 0 ? 'up' : 'down'}
              change={statistics.lowStockProducts}
            />
            <StatsCard
              title="Out of Stock"
              value={statistics.outOfStockProducts}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              trend={statistics.outOfStockProducts > 0 ? 'up' : 'down'}
              change={statistics.outOfStockProducts}
            />
          </div>
        )}
        
        {/* Filters */}
        <InventoryFilters 
          filters={filters}
          categories={allCategories}
          brands={allBrands}
          onFilterChange={handleFilterChange}
          loading={loading}
        />
        
        {/* Products Table */}
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-md">
          <InventoryTable 
            products={products}
            brands={allBrands}
            loading={loading}
            selectedProducts={selectedProducts}
            onSelectProduct={handleProductSelect}
            onSelectAll={handleSelectAll}
            onStockUpdate={handleStockUpdate}
            onDeleteProduct={handleDeleteProduct}
            onRefresh={handleRefresh}
            isMainAdmin={vendorProfile?.isMainAdmin === true}  // ADD THIS
          />
          
          {/* Results Info */}
          <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-700">
                {loading ? (
                  <span>Loading products...</span>
                ) : (
                  <span>
                    Showing <span className="font-medium">{products.length}</span> of{' '}
                    <span className="font-medium">{total}</span> products
                    {(filters.search || filters.category || filters.brand || filters.stockFilter !== 'all') && (
                      <span className="text-gray-500"> (filtered)</span>
                    )}
                  </span>
                )}
              </div>
              
              {selectedProducts.length > 0 && (
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{selectedProducts.length}</span> selected
                </div>
              )}
            </div>
            
            {/* Filter Summary */}
            {(filters.search || filters.category || filters.brand || filters.stockFilter !== 'all') && (
              <div className="mt-2 text-xs text-gray-500">
                Active filters: 
                {filters.search && <span className="ml-1 bg-gray-100 px-2 py-1 rounded">Search: "{filters.search}"</span>}
                {filters.category && <span className="ml-1 bg-gray-100 px-2 py-1 rounded">Category: {allCategories.find(c => c.id === filters.category)?.name || filters.category}</span>}
                {filters.brand && <span className="ml-1 bg-gray-100 px-2 py-1 rounded">Brand: {allBrands.find(b => b.id === filters.brand)?.name || filters.brand}</span>}
                {filters.stockFilter !== 'all' && <span className="ml-1 bg-gray-100 px-2 py-1 rounded">Stock: {filters.stockFilter}</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InventoryPage;